import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-iot-dashboard-tabs',
  templateUrl: './iot-dashboard-tabs.component.html',
  styleUrls: ['./iot-dashboard-tabs.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IotDashboardTabsComponent implements OnInit, OnChanges {
  @Input() currentView: string = 'dashboard';
  @Input() panelData: any = null; // Datos del panel IoT
  @Input() actuators: any[] = []; // Lista de actuadores/telares
  @Input() sensors: any[] = []; // Lista de sensores
  @Input() dataStreaming: any[] = []; // Datos en tiempo real
  @Input() registrosProduccion: any[] = []; // Registros de producción
  @Input() planList: any[] = []; // Lista de planes de acción (action-register)
  @Input() sensor: any = null; // Sensor seleccionado (action-register)
  @Input() type: string = ''; // Tipo de sensor (action-register)
  @Input() records: any[] = []; // Registros de producción (production-shift)
  @Input() idShift: number = 0; // ID del turno (production-shift)
  @Input() dateSelect: Date = new Date(); // Fecha seleccionada como objeto Date
  @Input() groupingType: string = 'actuator'; // Tipo de agrupación actual
  @Input() zoneSelect: number = 0; // Zona seleccionada
  @Input() selectedNomenclature: string = '0'; // Nomenclatura seleccionada
  @Input() enterpriseSelect: number = 0; // Empresa seleccionada
  @Input() filterUpdateTrigger: number = 0; // Trigger para actualizar filtros
  @Input() shifts: any[] = []; // Lista de turnos
  @Output() viewChange = new EventEmitter<string>();
  @Output() groupingChange = new EventEmitter<string>();

  tabs = [
    {
      id: 'telares',
      label: 'Telares',
      icon: 'device_hub',
      active: true
    },
    {
      id: 'registros',
      label: 'Registros',
      icon: 'assignment',
      active: false
    },
    {
      id: 'graficos',
      label: 'Gráficos',
      icon: 'bar_chart',
      active: false
    },
    {
      id: 'eficiencia',
      label: 'Eficiencia',
      icon: 'trending_up',
      active: false
    }
  ];

  // Propiedades cacheadas para optimizar rendimiento
  private _cachedTelaresData: any[] = [];
  private _cachedTelaresActivos: number = 0;
  private _lastDataUpdate: number = 0;
  private readonly CACHE_DURATION = 5000; // 5 segundos

  constructor(private cdr: ChangeDetectorRef) { }

  // Método para establecer el turno actual basado en la hora del sistema
  setCurrentShiftBasedOnTime() {
    if (!this.shifts || this.shifts.length === 0) {
      console.log('shifts no disponibles aún, reintentando en el próximo ciclo...');
      return;
    }
    
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour + (currentMinute / 60);
    
    let currentShift = 0; // Por defecto "Todos"
    
    // Buscar el turno que corresponde a la hora actual usando los datos dinámicos
    for (const shift of this.shifts) {
      if (shift.start && shift.finish) {
        const startTime = this.parseTimeToDecimal(shift.start);
        const finishTime = this.parseTimeToDecimal(shift.finish);
        
        // Verificar si la hora actual está dentro del rango del turno
        if (this.isTimeInShift(currentTime, startTime, finishTime)) {
          currentShift = shift.id;
          console.log(`Turno encontrado: ${shift.name} (${shift.start} - ${shift.finish})`);
          break;
        }
      }
    }
    
    console.log(`Hora actual: ${currentHour}:${currentMinute.toString().padStart(2, '0')} (${currentTime.toFixed(2)})`);
    const foundShift = this.shifts.find(s => s.id === currentShift);
    const shiftName = currentShift === 0 ? 'TODOS' : (foundShift ? foundShift.name : 'DESCONOCIDO');
    console.log(`Turno detectado: ${shiftName}`);
    
    // Establecer el turno en el panel padre si está disponible
    if (this.panelData && this.panelData.setShift) {
      this.panelData.setShift(currentShift);
      console.log(`Turno establecido automáticamente: ${currentShift}`);
    } else {
      console.log('Método setShift no disponible en panelData');
    }
  }

  // Método auxiliar para convertir tiempo HH:MM a decimal
  private parseTimeToDecimal(timeString: string): number {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours + (minutes / 60);
  }

  // Método auxiliar para verificar si la hora actual está dentro del rango del turno
  private isTimeInShift(currentTime: number, startTime: number, finishTime: number): boolean {
    // Si el turno no cruza medianoche (ej: 06:30 - 18:29)
    if (startTime < finishTime) {
      return currentTime >= startTime && currentTime < finishTime;
    } 
    // Si el turno cruza medianoche (ej: 18:30 - 06:29)
    else {
      return currentTime >= startTime || currentTime < finishTime;
    }
  }

  // Método público para forzar la detección del turno actual
  public forceShiftDetection() {
    this.setCurrentShiftBasedOnTime();
  }

  ngOnInit() {
    this.setActiveTab(this.currentView);
    this.setCurrentShiftBasedOnTime();
    
    // Reintentar establecer el turno si panelData no estaba disponible inicialmente
    setTimeout(() => {
      this.setCurrentShiftBasedOnTime();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Invalidar caché cuando los datos cambien
    if (changes['panelData'] || changes['actuators'] || changes['dataStreaming'] || 
        changes['filterUpdateTrigger'] || changes['selectedNomenclature'] || 
        changes['zoneSelect'] || changes['enterpriseSelect'] || changes['shifts']) {
      this.invalidateCache();
      this.cdr.markForCheck();
    }
    
    // Establecer el turno automáticamente cuando shifts estén disponibles
    if (changes['shifts'] && changes['shifts'].currentValue && changes['shifts'].currentValue.length > 0) {
      this.setCurrentShiftBasedOnTime();
    }
    
    // Forzar actualización si hay cambios en panelData
    if (changes['panelData'] && changes['panelData'].currentValue) {
      // Establecer el turno automáticamente cuando panelData esté disponible
      this.setCurrentShiftBasedOnTime();
      
      // Forzar recálculo de datos
      setTimeout(() => {
        this.cdr.markForCheck();
      }, 0);
    }
  }

  // Método para invalidar la caché cuando los datos cambien
  private invalidateCache() {
    this._lastDataUpdate = 0;
    this._cachedTelaresData = [];
    this._cachedTelaresActivos = 0;
    // console.log('Cache invalidated - clearing all cached data');
  }

  // Método para verificar si la caché es válida
  private isCacheValid(): boolean {
    return (Date.now() - this._lastDataUpdate) < this.CACHE_DURATION;
  }

  // Propiedades getter para el template
  get telaresData(): any[] {
    return this.getTelaresData();
  }

  get telaresActivos(): number {
    return this.getTelaresActivos();
  }

  get telaresDetenidos(): number {
    const total = this.telaresData.length;
    const activos = this.telaresActivos;
    const detenidos = total - activos;
    // console.log('telaresDetenidos - total:', total, 'activos:', activos, 'detenidos:', detenidos);
    return detenidos;
  }

  setActiveTab(tabId: string) {
    this.tabs.forEach(tab => {
      tab.active = tab.id === tabId;
    });
    this.currentView = tabId;
    this.viewChange.emit(tabId);
  }

  // Método público para forzar actualización de datos
  forceDataUpdate() {
    this.invalidateCache();
    this.cdr.markForCheck();
  }

  onTabClick(tabId: string) {
    this.setActiveTab(tabId);
    
    // Mapear los tabs a los tipos de agrupación
    const groupingMap: { [key: string]: string } = {
      'telares': 'actuator',
      'graficos': 'type',
      'registros': 'registros'
    };
    
    const newGroupingType = groupingMap[tabId];
    if (newGroupingType && newGroupingType !== this.groupingType) {
      // Solo emitir el evento si realmente es necesario
      // Para registros, no necesitamos cambiar el groupingType inmediatamente
      if (tabId !== 'registros') {
        this.groupingChange.emit(newGroupingType);
      }
    }
    
    // Si se cambia al tab de gráficos, regenerar los gráficos
    if (tabId === 'graficos' && this.panelData && this.panelData.generateTelarCharts) {
      setTimeout(() => {
        this.panelData.generateTelarCharts();
      }, 200);
    }
  }

  // Método para manejar cambios de fecha
  changeDate(event: any) {
    const selectedDate = event.value;
    if (selectedDate && this.panelData && this.panelData.changeDate) {
      this.panelData.changeDate(selectedDate);
    }
  }


  // Métodos para obtener datos reales del panel IoT


  getTabIcon(iconName: string): string {
    const icons: { [key: string]: string } = {
      'dashboard': '📊',
      'device_hub': '🏭',
      'bar_chart': '📈',
      'assignment': '📋',
      'warning': '⚠️',
      'schedule': '⏰'
    };
    return icons[iconName] || '📄';
  }


  // Métodos para obtener datos de producción
  getRegistrosData(): any[] {
    return this.registrosProduccion || [];
  }

  getTotalProduccion(): number {
    let total = 0;
    // Para registrosProduccion (panel IoT)
    if (this.registrosProduccion && this.registrosProduccion.length > 0) {
      total = this.registrosProduccion.reduce((total, registro) => total + (registro.distancia || 0), 0);
    }
    // Para records (production shift)
    else if (this.records && this.records.length > 0) {
      total = this.records.reduce((total, record) => total + (record.distance_quality || 0), 0);
    }
    
    return Math.round(total * 100) / 100; // Redondear a 2 decimales
  }

  // Método para formatear valores de sensores
  formatSensorValue(value: any, unit: string): string {
    if (value === null || value === undefined) {
      return `0 ${unit || ''}`.trim();
    }
    return `${value} ${unit || ''}`.trim();
  }

  // ===== MÉTODOS PARA INDICADORES OEE =====
  getCurrentDateTime(): string {
    const now = new Date();
    const time = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const date = now.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
    return `${time}, ${date}`;
  }

  getCurrentPeriod(): string {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4);
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long' });
    };
    
    return `${startDate.getDate()} al ${endDate.getDate()} de ${endDate.toLocaleDateString('es-ES', { month: 'long' })}`;
  }

  // Métodos para Disponibilidad
  getAvailabilityPercentage(): number {
    const availableTime = this.getAvailableTime();
    const productiveTime = this.getProductiveTime();
    
    if (availableTime === 0) return 0;
    
    const percentage = (productiveTime / availableTime) * 100;
    return Math.round(percentage * 100) / 100; // 2 decimales
  }

  // Función auxiliar para convertir segundos a horas
  private secondsToHours(seconds: number): number {
    return seconds / 3600; // 1 hora = 3600 segundos
  }

  // Función auxiliar para formatear horas en formato legible
  private formatHoursToReadable(hours: number): string {
    const totalSeconds = Math.round(hours * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    
    if (h > 0) {
      return `${h}h ${m}m ${s}s`;
    } else if (m > 0) {
      return `${m}m ${s}s`;
    } else {
      return `${s}s`;
    }
  }

  getAvailableTime(): number {
    if (!this.panelData || !this.panelData.shifts) return 8; // Valor por defecto de 8 horas
    
    const shifts = this.panelData.shifts;
    const selectedShift = this.panelData.shiftSelect;
    const telares = this.panelData.getOEEPerTelar();
    
    // Si no hay telares, usar valor por defecto
    if (!telares || telares.length === 0) return 8;
    
    var result = 0;
    if (selectedShift === 0) {
      // Si se selecciona "Todos", suma de todos los turnos × todos los telares
      const totalShiftHours = shifts.reduce((total, shift) => total + (shift.durationHour || 0), 0);
      result = totalShiftHours * telares.length;
      
      // Si no hay datos de turnos, usar valor por defecto
      if (totalShiftHours === 0) result = 8 * telares.length;
      
    } else {
      // Si se selecciona un turno específico, usar solo ese turno × cantidad de telares
      const selectedShiftData = shifts.find(shift => shift.id === selectedShift);
      const shiftHours = selectedShiftData ? (selectedShiftData.durationHour || 0) : 0;
      result = shiftHours * telares.length;
      
      // Si no hay datos del turno seleccionado, usar valor por defecto
      if (shiftHours === 0) result = 8 * telares.length;
    }
    
    // Restar totalDurationStopMinutes (convertido a horas)
    const totalStopMinutes = this.getTotalDurationStopMinutes();
    const totalStopHours = totalStopMinutes / 60; // Convertir minutos a horas
    result = Math.max(0, result - totalStopHours); // No permitir valores negativos
    
    return result;
    /*  // Si se selecciona un turno específico, suma de run_time - pause_time (convertido a horas)
      let totalRunTimeSeconds = 0;
      telares.forEach(telar => {
        console.log("cccc")
        if (telar.sensorValues) {
          const runTimeSensor = telar.sensorValues.find((sv: any) => 
            sv.nomenclature === 'run_time'
          );
          const pauseTimeSensor = telar.sensorValues.find((sv: any) => 
            sv.nomenclature === 'pause_time'
          );
          
          const runTime = runTimeSensor ? (runTimeSensor.value || 0) : 0;
          const pauseTime = pauseTimeSensor ? (pauseTimeSensor.value || 0) : 0;
          console.log(`Telar ${telar.name} - runTime: ${runTime}, pauseTime: ${pauseTime}`);
          totalRunTimeSeconds += (runTime - pauseTime);
        }
      });*/
     /* const result = this.secondsToHours(totalRunTimeSeconds);
      console.log('getAvailableTime - totalRunTimeSeconds:', totalRunTimeSeconds);
      console.log('getAvailableTime - result (Específico):', result);
      return result;*/
    //}
  }

  getProductiveTime(): number {
    if (!this.panelData) return 0;
    
    const telares = this.panelData.getOEEPerTelar();
    if (!telares || telares.length === 0) return 0;
    
    let totalProductiveTimeSeconds = 0;
    telares.forEach(telar => {
        const runTime = telar ? (telar.runTime || 0) : 0;
        const pauseTime = telar ? (telar.pauseTime || 0) : 0;
        totalProductiveTimeSeconds += (runTime - pauseTime);
    });
    
    // Si no hay datos de tiempo, usar un valor por defecto basado en el tiempo disponible
    if (totalProductiveTimeSeconds === 0) {
      const availableTime = this.getAvailableTime();
      return Math.max(0, availableTime * 0.8); // 80% del tiempo disponible como productivo
    }
    
    return this.secondsToHours(totalProductiveTimeSeconds);
  }

  getTotalDurationStopMinutes(): number {
    if (!this.panelData) return 0;
    
    // Buscar en los datos de los actuadores/telares
    const telares = this.getTelaresData();
    let totalStopMinutes = 0;
    
    console.log('getTotalDurationStopMinutes - telares:', telares);
    
    telares.forEach(telar => {
      if (telar && telar.sensorValues) {
        console.log('getTotalDurationStopMinutes - telar:', telar.name, 'sensorValues:', telar.sensorValues);
        
        // Buscar el sensor con totalDurationStopMinutes
        const stopSensor = telar.sensorValues.find((sv: any) => 
          sv.nomenclature == 'distance'
        );
        
        console.log('getTotalDurationStopMinutes - stopSensor:', stopSensor);
        
        if (stopSensor && stopSensor.totalDurationStopMinutes) {
          totalStopMinutes += stopSensor.totalDurationStopMinutes;
          console.log('getTotalDurationStopMinutes - added:', stopSensor.totalDurationStopMinutes, 'total:', totalStopMinutes);
        }
      }
    });
    
    console.log('getTotalDurationStopMinutes - final total:', totalStopMinutes);
    return totalStopMinutes;
  }

  getAvailableTimePercentage(): number {
    const availableTime = this.getAvailableTime();
    return availableTime > 0 ? 100 : 0; // Siempre 100% del tiempo disponible
  }

  getProductiveTimePercentage(): number {
    const percentage = (this.getProductiveTime() / this.getAvailableTime()) * 100;
    return Math.round(percentage * 100) / 100; // Redondear a 2 decimales
  }

  // Métodos para obtener valores formateados
  getAvailableTimeFormatted(): string {
    return this.formatHoursToReadable(this.getAvailableTime());
  }

  getProductiveTimeFormatted(): string {
    return this.formatHoursToReadable(this.getProductiveTime());
  }

  getStoppagesPercentage(): number {
    const stoppages = this.getAvailableTime() - this.getProductiveTime();
    const percentage = (stoppages / this.getAvailableTime()) * 100;
    return Math.round(percentage * 100) / 100; // Redondear a 2 decimales
  }

  // Métodos para Desempeño
  getPerformancePercentage(): number {
    if (!this.panelData) return 0;
    
    // Calcular rendimiento basándose en los valores reales de capacidad y producción
    const productiveCapacity = this.getProductiveCapacity();
    const realProduction = this.getRealProduction();
    
    if (productiveCapacity === 0) return 0;
    
    const performance = (realProduction / productiveCapacity) * 100;
    return Math.round(performance * 100) / 100; // Redondear a 2 decimales
  }

  getProductiveCapacity(): number {
    if (!this.panelData) return 0;
    
    const telares = this.panelData.getOEEPerTelar();
    if (!telares || telares.length === 0) return 0;
    
    let total = 0;
    telares.forEach((telar, index) => {
        const distance = telar ? (telar.projectedQuantity || 0) : 0;
        total += distance;
    });
    
    // Si no hay datos de capacidad proyectada, usar un valor por defecto
    if (total === 0) {
      const telaresCount = telares.length;
      return telaresCount * 100; // 100 metros por telar como valor por defecto
    }
    
    return Math.round(total * 100) / 100; // Redondear a 2 decimales
  }

  getRealProduction(): number {
    if (!this.panelData) return 0;
    const telares = this.panelData.getOEEPerTelar();
    if (!telares || telares.length === 0) return 0;
    
    let total = 0;
    telares.forEach(telar => {
        const distance = telar ? (telar.distance || 0) : 0;
        total += distance;
    });
    
    // Si no hay datos de producción real, usar un valor por defecto
    if (total === 0) {
      const telaresCount = telares.length;
      return telaresCount * 80; // 80 metros por telar como valor por defecto
    }
    
    return Math.round(total * 100) / 100; // Redondear a 2 decimales
  }

  getCapacityPercentage(): number {
    return 100; // 1000/1000 = 100%
  }

  getProductionPercentage(): number {
    const percentage = (this.getRealProduction() / this.getProductiveCapacity()) * 100;
    return Math.round(percentage * 100) / 100; // Redondear a 2 decimales
  }

  getSpeedReductionPercentage(): number {
    const reduction = this.getProductiveCapacity() - this.getRealProduction();
    const percentage = (reduction / this.getProductiveCapacity()) * 100;
    return Math.round(percentage * 100) / 100; // Redondear a 2 decimales
  }

  // Métodos para Calidad
  getQualityPercentage(): number {
    const realProduction = this.getRealProduction();
    const goodProduction = this.getGoodProduction();
    
    if (realProduction === 0) return 0;
    
    const qualityPercentage = (goodProduction / realProduction) * 100;
    return Math.round(qualityPercentage * 100) / 100; // Redondear a 2 decimales
  }

  getGoodProduction(): number {
    if (!this.panelData) return 0;
    
  /*  const telares = this.panelData.getOEEPerTelar();
    let totalQualityQuantity = 0;
    
    telares.forEach(telar => {
      if (telar && telar.qualityQuantity) {
        totalQualityQuantity += telar.qualityQuantity;
      }
    });*/

    const telares = this.panelData.getOEEPerTelar();
    let total = 0;
    telares.forEach(telar => {
        const distance = telar ? (telar.distance || 0) : 0;
        total += distance;
    });
    
    return Math.round(total * 100) / 100; // Redondear a 2 decimales
    
    ///return totalQualityQuantity;
  }

  getGoodProductionPercentage(): number {
    const realProduction = this.getRealProduction();
    const goodProduction = this.getGoodProduction();
    
    if (realProduction === 0) return 0;
    
    const percentage = (goodProduction / realProduction) * 100;
    return Math.round(percentage * 100) / 100; // Redondear a 2 decimales
  }

  getNonConformingPercentage(): number {
    const realProduction = this.getRealProduction();
    const goodProduction = this.getGoodProduction();
    const nonConforming = realProduction - goodProduction;
    
    if (realProduction === 0) return 0;
    
    const percentage = (nonConforming / realProduction) * 100;
    return Math.round(percentage * 100) / 100; // Redondear a 2 decimales
  }

  // Métodos para OEE
  getOEEPercentage(): number {
    // Calcular OEE basándose en los valores reales de Disponibilidad, Rendimiento y Calidad
    const availability = this.getAvailabilityPercentage();
    const performance = this.getPerformancePercentage();
    const quality = this.getQualityPercentage();
    
    // OEE = (Disponibilidad × Rendimiento × Calidad) / 10000
    const oee = (availability * performance * quality) / 10000;
    
    return Math.round(oee * 100) / 100; // Redondear a 2 decimales
  }

  // ===== MÉTODOS PARA DATOS REALES =====
  getTelaresData(): any[] {
    // Siempre obtener datos frescos para asegurar actualización con filtros
    let data: any[] = [];
    
    if (this.actuators && this.actuators.length > 0) {
      data = this.actuators;
    } else if (this.panelData && this.panelData.getFilteredActuatorsAlternative) {
      data = this.panelData.getFilteredActuatorsAlternative();
    }

    // Actualizar caché solo para referencia
    this._cachedTelaresData = data;
    this._lastDataUpdate = Date.now();
    
    return data;
  }

  getTelaresActivos(): number {
    const telares = this.getTelaresData();
    
    // Si no hay telares, retornar 0
    if (!telares || telares.length === 0) return 0;
    
    const activos = telares.filter(telar => {
      // Usar la nueva lógica de status
      const status = this.getTelarMetric(telar, 'status');
      return status === 'En Marcha';
    }).length;

    console.log(`Telares activos: ${activos} de ${telares.length} total`);

    // Actualizar caché solo para referencia
    this._cachedTelaresActivos = activos;
    
    return activos;
  }


  getTelarStatusClass(telar: any): string {
    const status = this.getTelarMetric(telar,'status');
    return status === 'En Marcha' ? 'status-active' : 'status-inactive';
  }

  // Método para obtener métricas específicas del telar
  getTelarMetric(telar: any, metricType: string): any {
    if (!telar || !telar.sensorValues) {
      // Si no hay datos, retornar 0 pero el estado será "En Marcha"
      switch(metricType) {
        case 'metros_lineales_tela': return 0;
        case 'metros_proyectados_tela': return 0;
        case 'tiempo_pausa': return '0h 0m 0s';
        case 'tiempo_marcha': return '0h 0m 0s';
        case 'rendimiento': return 0;
        default: return 0;
      }
    }
    
    // Verificar si todos los datos están en 0
    const allDataZero = telar.sensorValues.every((sv: any) => {
      const value = parseFloat(sv.value);
      return isNaN(value) || value === 0 || sv.value === null || sv.value === undefined || sv.value === '';
    });
    
    // Si todos los datos están en 0, retornar 0 pero el estado será "En Marcha"
    if (allDataZero) {
      console.log(`Telar ${telar.name} - Todos los datos están en 0, retornando 0 para ${metricType} pero estado será "En Marcha"`);
      switch(metricType) {
        case 'metros_lineales_tela': return 0;
        case 'metros_proyectados_tela': return 0;
        case 'tiempo_pausa': return '0h 0m 0s';
        case 'tiempo_marcha': return '0h 0m 0s';
        case 'rendimiento': return 0;
        default: return 0;
      }
    }
    
    // Si hay datos reales, usar los valores reales
    switch(metricType) {
      case 'metros_lineales_tela':
        const metrosMetric = telar.sensorValues.find(sv => 
          (sv.name && sv.name.toLowerCase().includes('distance')) || 
          (sv.nomenclature && sv.nomenclature.toLowerCase().includes('distance'))
        );
        return metrosMetric ? metrosMetric.value : 0;
      
      case 'metros_proyectados_tela':
          const metrosProjectMetric = telar.sensorValues.find(sv => 
            (sv.name && sv.name.toLowerCase().includes('distance')) || 
            (sv.nomenclature && sv.nomenclature.toLowerCase().includes('distance'))
          );
          return metrosProjectMetric ? metrosProjectMetric.projectedQuantity : 0;

      case 'tiempo_pausa':
        const pausaMetric = telar.sensorValues.find(sv => 
          (sv.name && sv.name.toLowerCase().includes('pause_time')) || 
          (sv.nomenclature && sv.nomenclature.toLowerCase().includes('pause_time'))
        );
        if (pausaMetric && pausaMetric.value) {
          return this.formatTimeFromSeconds(pausaMetric.value);
        }
        return '0h 0m 0s';
      
      case 'tiempo_marcha':
        const marchaMetric = telar.sensorValues.find(sv => 
          (sv.name && sv.name.toLowerCase().includes('run_time')) || 
          (sv.nomenclature && sv.nomenclature.toLowerCase().includes('run_time'))
        );
        if (marchaMetric && marchaMetric.value) {
          return this.formatTimeFromSeconds(marchaMetric.value);
        }
        return '0h 0m 0s';
      
       case 'rpm':
                  const rpmMetric = telar.sensorValues.find(sv => 
                    (sv.name && sv.name.toLowerCase().includes('pass_minute')) || 
                    (sv.nomenclature && sv.nomenclature.toLowerCase().includes('pass_minute'))
                  );
                  return rpmMetric ? parseFloat(rpmMetric.value) || 0 : 0;
                
        case 'rendimiento':
                    return this.getTelarPerformance(telar);
        case 'status':
              const statusMetric = telar.sensorValues.find(sv => 
                (sv.name && sv.name.toLowerCase().includes('status')) || 
                (sv.nomenclature && sv.nomenclature.toLowerCase().includes('status'))
              );
              return statusMetric ? statusMetric.value == 'on' ? 'En Marcha' : 'Detenido' : '-';
       case 'status_value':
                const statusMetricValue = telar.sensorValues.find(sv => 
                  (sv.name && sv.name.toLowerCase().includes('status')) || 
                  (sv.nomenclature && sv.nomenclature.toLowerCase().includes('status'))
                );
                return statusMetricValue ? statusMetricValue.value : '-';
        default:
        return 0;
    }
  }

  // Método para formatear tiempo desde segundos
  formatTimeFromSeconds(seconds: number): string {
    if (!seconds || seconds === 0) return '0h 0m 0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${hours}h ${minutes}m ${secs}s`;
  }

  // Método para calcular el rendimiento individual de cada telar
  getTelarPerformance(telar: any): number {
    if (!telar) return 0;
    
    // Obtener el tiempo de máquina en marcha (run_time) en segundos
    let runTimeSeconds = 0;
    if (telar.sensorValues) {
      const runTimeSensor = telar.sensorValues.find((sv: any) => 
        sv.nomenclature === 'run_time' || 
        (sv.name && sv.name.toLowerCase().includes('tiempo de máquina en marcha'))
      );
      
      if (runTimeSensor && runTimeSensor.value) {
        runTimeSeconds = parseFloat(runTimeSensor.value) || 0;
      }
    }
    
    // Calcular minutos reales encendido (diferencia entre inicio del turno y hora actual)
    const minutosRealesEncendido = this.getMinutosRealesEncendido();
    
    // Validaciones de datos
    if (minutosRealesEncendido === 0) {
      console.warn(`getTelarPerformance - Minutos reales encendido es 0 para telar: ${telar.name || 'Desconocido'}`);
      return 0;
    }
    
    if (runTimeSeconds < 0) {
      console.warn(`getTelarPerformance - Tiempo de máquina en marcha negativo (${runTimeSeconds}) para telar: ${telar.name || 'Desconocido'}`);
      return 0;
    }
    
    // Convertir run_time de segundos a minutos
    const runTimeMinutes = runTimeSeconds / 60;
    
    // Calcular rendimiento: (Tiempo de Máquina en Marcha / Minutos Reales Encendido) × 100
    let performance = (runTimeMinutes / minutosRealesEncendido) * 100;
    // Limitar el rendimiento al 100% máximo (lógicamente no puede superar el 100%)
    if (performance > 100) {
      console.warn(`getTelarPerformance - Rendimiento calculado superior al 100% (${performance.toFixed(2)}%) para telar: ${telar.name || 'Desconocido'}`);
      console.warn(`  - Run time: ${runTimeMinutes.toFixed(2)} min, Minutos reales: ${minutosRealesEncendido.toFixed(2)} min`);
      console.warn(`  - Limitando a 100%`);
      performance = 100;
    }
    
    // Logging detallado para debugging
    console.log(`getTelarPerformance - Telar: ${telar.name || 'Desconocido'}`);
    console.log(`  - Tiempo de máquina en marcha: ${runTimeMinutes.toFixed(2)} minutos`);
    console.log(`  - Minutos reales encendido: ${minutosRealesEncendido.toFixed(2)} minutos`);
    console.log(`  - Rendimiento calculado: ${performance.toFixed(2)}%`);
    
    return Math.round(performance); // Redondear a entero
  }

  // Método para calcular los minutos reales encendido (diferencia entre inicio del turno y hora actual)
  private getMinutosRealesEncendido(): number {
    if (!this.shifts || this.shifts.length === 0) {
      console.warn('No hay turnos disponibles para calcular minutos reales encendido');
      return 0;
    }
    
    const now = new Date();
    const currentTime = now.getHours() + (now.getMinutes() / 60);
    
    let currentShift = null;
    let shiftStartTime = new Date();
    
    // Verificar si estamos en modo "Todos los turnos" (shiftSelect = 0)
    if (this.panelData && this.panelData.shiftSelect === 0) {
      // Modo "Todos": usar el inicio del primer turno del día (06:30 AM)
      const firstShift = this.shifts.find(shift => shift.start);
      if (firstShift) {
        const startTime = this.parseTimeToDecimal(firstShift.start);
        const startHour = Math.floor(startTime);
        const startMinute = Math.round((startTime - startHour) * 60);
        
        shiftStartTime.setHours(startHour, startMinute, 0, 0);
        currentShift = { name: 'TODOS', start: firstShift.start, finish: '23:59' };
        
        console.log('Modo "Todos los turnos" - usando inicio del primer turno');
      }
    } else {
      // Modo turno específico: buscar el turno actual
      for (const shift of this.shifts) {
        if (shift.start && shift.finish) {
          const startTime = this.parseTimeToDecimal(shift.start);
          const finishTime = this.parseTimeToDecimal(shift.finish);
          
          if (this.isTimeInShift(currentTime, startTime, finishTime)) {
            currentShift = shift;
            break;
          }
        }
      }
      
      if (currentShift) {
        // Calcular la hora de inicio del turno para hoy
        const startTime = this.parseTimeToDecimal(currentShift.start);
        const startHour = Math.floor(startTime);
        const startMinute = Math.round((startTime - startHour) * 60);
        
        shiftStartTime.setHours(startHour, startMinute, 0, 0);
        
        // Si el turno cruza medianoche y estamos en la parte nocturna, ajustar la fecha
        if (startTime > this.parseTimeToDecimal(currentShift.finish)) {
          // Turno nocturno que cruza medianoche
          if (now.getHours() < startHour) {
            // Estamos en la parte nocturna del turno (después de medianoche)
            shiftStartTime.setDate(shiftStartTime.getDate() - 1);
          }
        }
      }
    }
    
    if (!currentShift) {
      console.warn('No se encontró turno actual para calcular minutos reales encendido');
      return 0;
    }
    
    // Calcular diferencia en minutos (considerando horas, minutos y segundos)
    const diffMs = now.getTime() - shiftStartTime.getTime();
    const diffMinutes = diffMs / (1000 * 60); // Convertir milisegundos a minutos
    
    // Cálculo más preciso considerando todos los componentes de tiempo
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutesRemainder = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    const totalMinutesPrecise = diffHours * 60 + diffMinutesRemainder + (diffSeconds / 60);
    
    console.log(`Cálculo detallado de minutos reales encendido:`);
    console.log(`  - Diferencia en milisegundos: ${diffMs}`);
    console.log(`  - Horas: ${diffHours}`);
    console.log(`  - Minutos: ${diffMinutesRemainder}`);
    console.log(`  - Segundos: ${diffSeconds}`);
    console.log(`  - Total en minutos (preciso): ${totalMinutesPrecise.toFixed(2)}`);
    console.log(`  - Total en minutos (simple): ${diffMinutes.toFixed(2)}`);
    
    // Alert temporal para verificar el cálculo
    console.log(`Minutos reales encendido calculados: ${totalMinutesPrecise.toFixed(2)} minutos`);
    console.log(`  - Inicio del turno: ${shiftStartTime.toLocaleString()}`);
    console.log(`  - Hora actual: ${now.toLocaleString()}`);
    console.log(`  - Modo: ${currentShift.name} (${currentShift.start} - ${currentShift.finish})`);
    
    return Math.max(0, totalMinutesPrecise); // No permitir valores negativos
  }

  // Métodos para gráficos
  getTotalTiempoActivo(): string {
    if (this.panelData && this.panelData.getTotalTiempoActivo) {
      return this.panelData.getTotalTiempoActivo();
    }
    return '0h 0m 0s';
  }

  getTotalDistancia(): string {
    if (this.panelData && this.panelData.getTotalDistancia) {
      return this.panelData.getTotalDistancia();
    }
    return '0 m';
  }

  getSensoresData(): any[] {
    if (this.sensors && this.sensors.length > 0) {
      return this.sensors;
    }
    if (this.panelData && this.panelData.sensors) {
      return this.panelData.sensors;
    }
    return [];
  }

  getSensoresActivos(): number {
    const sensores = this.getSensoresData();
    return sensores.filter(sensor => sensor.state === 1).length;
  }

  getSensorStatus(sensor: any): string {
    switch(sensor.state) {
      case 1: return 'Activo';
      case 2: return 'Error';
      default: return 'Inactivo';
    }
  }

  getSensorStatusClass(sensor: any): string {
    switch(sensor.state) {
      case 1: return 'status-active';
      case 2: return 'status-error';
      default: return 'status-inactive';
    }
  }

  getVisibleSensorValues(sensorValues: any[]): any[] {
    if (!sensorValues) return [];
    return sensorValues.filter(sv => sv.value !== null && sv.value !== undefined);
  }

  formatDateTime(dateTimeString: string): string {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getDuration(fechaInicio: string, fechaFin: string): string {
    if (!fechaInicio || !fechaFin) return '';
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diffMs = fin.getTime() - inicio.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMinutes}m`;
  }

  // ===== MÉTODOS PARA GRÁFICOS (replicando panel-iot) =====
  setGroupingType(type: string) {
    if (this.panelData && this.panelData.setGroupingType) {
      this.panelData.setGroupingType(type);
    }
  }

  trackByActuator(index: number, actuator: any): any {
    return actuator ? actuator.id : index;
  }

  isOnValue(value: any): boolean {
    return value === 'on' || value === 'ON' || value === 1;
  }

  isOffValue(value: any): boolean {
    return value === 'off' || value === 'OFF' || value === 0;
  }

  hasVibrationData(actuator: any): boolean {
    if (!actuator || !actuator.sensorValues) return false;
    return actuator.sensorValues.some((sv: any) => 
      sv.name && sv.name.toLowerCase().includes('vibración')
    );
  }

  getVibrationRMS(actuator: any): number {
    if (!actuator || !actuator.sensorValues) return 0;
    const vibrationSensor = actuator.sensorValues.find((sv: any) => 
      sv.name && sv.name.toLowerCase().includes('vibración')
    );
    return vibrationSensor ? parseFloat(vibrationSensor.value) || 0 : 0;
  }

  getVibrationStatusClass(rmsValue: number): string {
    if (rmsValue < 2.8) return 'status-good';
    if (rmsValue < 7.1) return 'status-warning';
    return 'status-danger';
  }

  getVibrationStatusText(rmsValue: number): string {
    if (rmsValue < 2.8) return 'Bueno';
    if (rmsValue < 7.1) return 'Aceptable';
    return 'Crítico';
  }

  hasDistanceData(actuator: any): boolean {
    if (!actuator || !actuator.sensorValues) return false;
    return actuator.sensorValues.some((sv: any) => 
      sv.name && sv.name.toLowerCase().includes('distancia')
    );
  }

  getDistanceDataDate(actuator: any): string {
    if (!actuator || !actuator.sensorValues) return '';
    
    // Buscar específicamente el sensor run_time (tiempo de máquina en marcha)
    const runTimeSensor = actuator.sensorValues.find((sv: any) => sv.nomenclature === 'run_time');
   
    if (runTimeSensor && runTimeSensor.valueDate) {
      // Formatear la fecha para mostrar
       //console.log("runTimeSensor ||| ", runTimeSensor.valueDate);
      /*const date = new Date(runTimeSensor.valueDate);
      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });*/
      return runTimeSensor.valueDate;
    }
    /*
    // Si no encuentra run_time, buscar el sensor con valueDate más reciente como fallback
    let latestSensor = null;
    let latestDate = null;
    
    actuator.sensorValues.forEach((sv: any) => {
      if (sv.valueDate) {
        const sensorDate = new Date(sv.valueDate);
        if (!latestDate || sensorDate > latestDate) {
          latestDate = sensorDate;
          latestSensor = sv;
        }
      }
    });
    
    if (latestSensor && latestSensor.valueDate) {
      const date = new Date(latestSensor.valueDate);
      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }*/
    
    return 'Sin datos';
  }

  openModalGraph(item: any) {
    if (this.panelData && this.panelData.openModalGraph) {
      this.panelData.openModalGraph(item);
    }
  }

  getOEEPerTelar(): any[] {
    if (this.panelData && this.panelData.getOEEPerTelar) {
      return this.panelData.getOEEPerTelar();
    }
    return [];
  }

  getOEEStatusClass(type: string, value?: number): string {
    if (this.panelData && this.panelData.getOEEStatusClass) {
      return this.panelData.getOEEStatusClass(type, value);
    }
    return '';
  }

  getOEEStatusText(): string {
    if (this.panelData && this.panelData.getOEEStatusText) {
      return this.panelData.getOEEStatusText();
    }
    return '';
  }

  getOEERecommendation(): string {
    if (this.panelData && this.panelData.getOEERecommendation) {
      return this.panelData.getOEERecommendation();
    }
    return '';
  }

  getVibrationSummary(): any {
    if (this.panelData && this.panelData.getVibrationSummary) {
      return this.panelData.getVibrationSummary();
    }
    return null;
  }


  getVibrationRecommendation(): string {
    if (this.panelData && this.panelData.getVibrationRecommendation) {
      return this.panelData.getVibrationRecommendation();
    }
    return '';
  }


  // Método para obtener el color del estado
  getStatusColor(actuator: any): string {
    if (actuator.state === 1) return 'green';
    if (actuator.state === 0) return 'red';
    return 'gray';
  }

  // ===== MÉTODOS PARA ACTION REGISTER =====
  getTotalAlertas(): number {
    return this.planList ? this.planList.length : 0;
  }

  getAlertasActivas(): number {
    if (!this.planList) return 0;
    return this.planList.filter(plan => plan.isEnabled === '1').length;
  }

  getAlertasPendientes(): number {
    if (!this.planList) return 0;
    return this.planList.filter(plan => plan.isEnabled === '1' && plan.actionPlanAt == null).length;
  }

  getAlertasCompletadas(): number {
    if (!this.planList) return 0;
    return this.planList.filter(plan => plan.actionPlanAt != null).length;
  }

  getSensorInfo(): string {
    if (!this.sensor) return 'Sensor no seleccionado';
    return `Zona ${this.sensor.zone} - Sensor ${this.sensor.sensor}`;
  }

  getTipoSensor(): string {
    return this.type || 'Tipo no especificado';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getAlertaStatus(plan: any): string {
    if (plan.isEnabled === '0') return 'Deshabilitada';
    if (plan.actionPlanAt != null) return 'Completada';
    return 'Pendiente';
  }

  getAlertaStatusColor(plan: any): string {
    if (plan.isEnabled === '0') return 'gray';
    if (plan.actionPlanAt != null) return 'green';
    return 'orange';
  }

  // ===== MÉTODOS PARA PRODUCTION SHIFT =====
  getTotalRegistros(): number {
    return this.records ? this.records.length : 0;
  }

  getRegistrosActivos(): number {
    if (!this.records) return 0;
    return this.records.filter(record => record.estado === 'Activo' || !record.estado).length;
  }

  getRegistrosCompletados(): number {
    if (!this.records) return 0;
    return this.records.filter(record => record.estado === 'Completado').length;
  }


  getTotalParadas(): number {
    if (!this.records) return 0;
    return this.records.reduce((total, record) => total + (record.scheduledStops ? record.scheduledStops.length : 0), 0);
  }

  getActuadoresDisponibles(): number {
    return this.actuators ? this.actuators.length : 0;
  }

  getTurnoInfo(): string {
    switch(this.idShift) {
      case 1: return 'Turno Día';
      case 2: return 'Turno Noche';
      default: return 'Todos los Turnos';
    }
  }

  getRegistroStatus(record: any): string {
    return record.estado || 'Activo';
  }

  getRegistroStatusColor(record: any): string {
    const status = record.estado || 'Activo';
    switch(status) {
      case 'Completado': return 'green';
      case 'Pausado': return 'orange';
      case 'Cancelado': return 'red';
      default: return 'blue';
    }
  }

  getActuadorName(record: any): string {
    if (!record.idActuator || !this.actuators) return 'N/A';
    const actuator = this.actuators.find(act => act.id === record.idActuator);
    return actuator ? (actuator.name || actuator.sensor || actuator.nomenclature) : 'N/A';
  }

  // Métodos auxiliares para simplificar las expresiones del template
  getPromedioPorRegistro(): number {
    const result = this.getTotalRegistros() > 0 ? (this.getTotalProduccion() / this.getTotalRegistros()) : 0;
    return Math.round(result * 100) / 100; // Redondear a 2 decimales
  }

  getEficienciaPorcentaje(): number {
    return this.getTotalRegistros() > 0 ? ((this.getRegistrosCompletados() / this.getTotalRegistros()) * 100) : 0;
  }

  getActivosPorcentaje(): number {
    return this.getTotalRegistros() > 0 ? ((this.getRegistrosActivos() / this.getTotalRegistros()) * 100) : 0;
  }

  getParadasPorRegistro(): number {
    const result = this.getTotalRegistros() > 0 ? (this.getTotalParadas() / this.getTotalRegistros()) : 0;
    return Math.round(result * 100) / 100; // Redondear a 2 decimales
  }

  getRegistrosPorActuador(actuatorId: number): number {
    return this.records ? this.records.filter(r => r.idActuator === actuatorId).length : 0;
  }

  getProduccionPorActuador(actuatorId: number): number {
    if (!this.records) return 0;
    const result = this.records.filter(r => r.idActuator === actuatorId).reduce((sum, r) => sum + (r.distance_quality || 0), 0);
    return Math.round(result * 100) / 100; // Redondear a 2 decimales
  }

  getPromedioPorActuador(actuatorId: number): number {
    const registros = this.getRegistrosPorActuador(actuatorId);
    const result = registros > 0 ? (this.getProduccionPorActuador(actuatorId) / registros) : 0;
    return Math.round(result * 100) / 100; // Redondear a 2 decimales
  }

  getParadasPorActuador(actuatorId: number): number {
    if (!this.records) return 0;
    return this.records.filter(r => r.idActuator === actuatorId).reduce((sum, r) => sum + (r.scheduledStops ? r.scheduledStops.length : 0), 0);
  }
}
